import isNil from 'lodash/isNil';
import DividendDiscountModel from './DividendDiscountModel';
import ExcessReturnsModel from './ExcessReturnsModel';
import REITModel from './REITModel';
import TwoStageFCFModel from './TwoStageFCFModel';

import type { IntrinsicValueSummaryProps } from './IntrinsicValueSummary';

enum DiscountModel {
  ExcessReturnsModel = 'Excess Returns Model',
  ReitAdjusted = '2 Stage Free Cash Flow to Equity using Adjusted Funds From Operations',
  ReitFunds = '2 Stage Free Cash Flow to Equity using Funds From Operations',
  ReitNetAssetValue = 'Analysts Estimates of Net Asset Value',
  DividendDiscountModel = 'Dividend Discount Model',
  TwoStageFreeCashFlow = '2 Stage Free Cash Flow to Equity',
}

type Props = Pick<
  IntrinsicValueSummaryProps,
  | 'uniqueSymbol'
  | 'reportingCurrencyISO'
  | 'reportingCurrencySymbol'
  | 'companyShortName'
  | 'relativeListingSharesOutstanding'
  | 'riskFreeRate'
  | 'costOfEquity'
  | 'pv5y'
  | 'terminalValue'
  | 'pvtv'
  | 'npv'
  | 'npvPerShare'
  | 'npvPerShareReportedCurrency'
  | 'REIT'
  | 'dividendDiscount'
  | 'twoStagefcf'
  | 'excessReturns'
  | 'model'
>;

const IntrinsicModel = ({
  uniqueSymbol,
  reportingCurrencyISO,
  reportingCurrencySymbol,
  companyShortName,
  relativeListingSharesOutstanding,
  riskFreeRate,
  costOfEquity,
  pv5y,
  terminalValue,
  pvtv,
  npv,
  npvPerShare,
  npvPerShareReportedCurrency,
  REIT,
  dividendDiscount,
  twoStagefcf,
  excessReturns,
  model,
}: Props) => {
  if (!isNil(excessReturns) && model.includes(DiscountModel.ExcessReturnsModel))
    return (
      <ExcessReturnsModel
        uniqueSymbol={uniqueSymbol}
        reportingCurrencyISO={reportingCurrencyISO}
        reportingCurrencySymbol={reportingCurrencySymbol}
        companyShortName={companyShortName}
        riskFreeRate={riskFreeRate}
        costOfEquity={costOfEquity}
        pvtv={pvtv}
        npv={npv}
        excessReturns={excessReturns}
      />
    );
  else if (
    !isNil(REIT) &&
    (model.includes(DiscountModel.ReitAdjusted) ||
      model.includes(DiscountModel.ReitFunds) ||
      model.includes(DiscountModel.ReitNetAssetValue))
  )
    return (
      <REITModel
        uniqueSymbol={uniqueSymbol}
        relativeListingSharesOutstanding={relativeListingSharesOutstanding}
        reportingCurrencyISO={reportingCurrencyISO}
        companyShortName={companyShortName}
        npvPerShare={npvPerShare}
        REIT={REIT}
      />
    );
  else if (
    !isNil(twoStagefcf) &&
    !isNil(twoStagefcf.fcfData) &&
    model.includes(DiscountModel.TwoStageFreeCashFlow)
  )
    return (
      <TwoStageFCFModel
        uniqueSymbol={uniqueSymbol}
        reportingCurrencyISO={reportingCurrencyISO}
        reportingCurrencySymbol={reportingCurrencySymbol}
        companyShortName={companyShortName}
        relativeListingSharesOutstanding={relativeListingSharesOutstanding}
        riskFreeRate={riskFreeRate}
        costOfEquity={costOfEquity}
        pv5y={pv5y}
        terminalValue={terminalValue}
        pvtv={pvtv}
        npv={npv}
        npvPerShare={npvPerShare}
        npvPerShareReportedCurrency={npvPerShareReportedCurrency}
        twoStagefcf={twoStagefcf}
      />
    );
  else if (
    !isNil(dividendDiscount) &&
    !isNil(dividendDiscount.DDMGrowth) &&
    model.includes(DiscountModel.DividendDiscountModel)
  )
    return (
      <DividendDiscountModel
        uniqueSymbol={uniqueSymbol}
        reportingCurrencySymbol={reportingCurrencySymbol}
        companyShortName={companyShortName}
        dividendPerpetualGrowthRate={dividendDiscount.DDMGrowth}
        costOfEquity={costOfEquity}
        DPS={dividendDiscount.DPS}
      />
    );
  else return null;
};
export default IntrinsicModel;
